import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';
import './SceneProseView.css';
import AIEditModal from './AIEditModal';
import Spinner from './Spinner';

export const SceneProseView = (props) => {
  const { novelId, chapterId, sceneId } = useParams();
  const navigate = useNavigate();
  const [scene, setScene] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [novel, setNovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialContent, setInitialContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [user, setUser] = useState(null);
  
  // Add state for managing save notification after AI edit
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [savingAiEdit, setSavingAiEdit] = useState(false);
  
  // Add state for AI edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectedRange, setSelectedRange] = useState(null);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  
  // Use ref to directly access the quill editor
  const quillRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  
  // Function to handle AI Assistant button click
  const handleAIAssistantClick = useCallback(() => {
    if (!quillRef.current) return;
    
    const quill = quillRef.current.getEditor();
    const selection = quill.getSelection();
    
    if (selection && selection.length > 0) {
      // Get the selected text
      const text = quill.getText(selection.index, selection.length);
      setSelectedText(text);
      setSelectedRange(selection);
    } else {
      // No selection, use entire content
      setSelectedText('');
      setSelectedRange(null);
    }
    
    // Open the modal
    setIsModalOpen(true);
  }, []);
  
  // Function to handle AI edit submission
  const handleAIEditSubmit = async (instruction) => {
    if (!quillRef.current) return;
    
    try {
      setIsProcessingAI(true);
      
      const quill = quillRef.current.getEditor();
      const currentProse = quill.root.innerHTML;
      
      // Determine if we're editing a selection or the whole content
      const highlightedText = selectedRange 
        ? quill.getText(selectedRange.index, selectedRange.length) 
        : '';
      
      // Make the API call
      const result = await api.requestAIEdit(
        novelId,
        chapterId,
        sceneId,
        currentProse,
        instruction,
        highlightedText
      );
      
      if (result.success) {
        if (result.isTargetedEdit && selectedRange) {
          // Replace just the selected text
          quill.deleteText(selectedRange.index, selectedRange.length);
          quill.insertText(selectedRange.index, result.replacementText);
          
          // Format the inserted text if needed
          // (This would depend on your specific requirements)
        } else {
          // Replace the entire content
          quill.setContents([]);
          quill.clipboard.dangerouslyPasteHTML(result.replacementText);
        }
        
        // Show notification about unsaved changes
        setShowSaveNotification(true);
        setHasUnsavedChanges(true);
        
        // Close the modal
        setIsModalOpen(false);
      } else {
        setError('Failed to get AI edit suggestions. Please try again.');
      }
    } catch (error) {
      console.error('Error processing AI edit:', error);
      setError('An error occurred while processing your AI edit request.');
    } finally {
      setIsProcessingAI(false);
    }
  };
  
  // Configure Quill modules including custom handler
  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        ['clean'],
        ['ai-assistant']
      ],
      handlers: {
        'ai-assistant': handleAIAssistantClick
      }
    }
  };

  useEffect(() => {
    // Add custom button to Quill's toolbar after component mounts
    const addCustomButton = () => {
      // Find all toolbar buttons
      const toolbarButtons = document.querySelectorAll('.ql-toolbar button');
      
      // Find our specific button (the last one we added)
      const aiButton = Array.from(toolbarButtons).find(button => 
        button.className.includes('ql-ai-assistant')
      );
      
      if (aiButton) {
        // Style our custom button
        aiButton.textContent = 'AI Assistant';
        aiButton.className += ' custom-ai-button';
      }
    };
    
    // Add button with a small delay to ensure Quill has initialized
    const timer = setTimeout(() => {
      addCustomButton();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch novel details
        const novelResult = await api.getNovelById(novelId);
        if (novelResult.success) {
          setNovel(novelResult.novel);
        }

        // Fetch user details
        setUser(JSON.parse(localStorage.getItem('user')));

        console.log(props.scene);
        setChapter(props.chapter);
        setScene(props.scene);
        const html = convertMarkdownToHtml(props.scene.content || '');
        setInitialContent(html);

      } catch (error) {
        console.error("Failed to fetch data:", error);
        setError("Failed to load scene data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Setup beforeunload event to warn about unsaved changes
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Clear any pending save timeouts
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [novelId, chapterId, sceneId]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm("You have unsaved changes. Are you sure you want to leave?");
      if (!confirmLeave) return;
    }
    navigate(`/novel/${novelId}/chapter/${chapterId}/scenes`);
  };

  const isReadOnly = () => {
    if (!user) return true;
    
    // Check if subscription is active
    if (user.subscription_active) return false;
    
    // Check if trial period is still valid
    if (user.trial_end_date) {
      const trialEndDate = new Date(user.trial_end_date);
      const currentDate = new Date();
      return currentDate > trialEndDate;
    }
    
    return true;
  };

  // Setup auto-save when content changes
  const handleContentChange = () => {
    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);
    
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Setup debounced save
    saveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 2000); // 2 second debounce
  };

  // Function to save edits
  const handleSave = async () => {
    // Don't attempt to save if we're already saving
    if (saving) return;
    
    try {
      setSaving(true);
      setSaveStatus('saving');
      
      // Get current content directly from the editor
      const currentContent = quillRef.current?.getEditor().root.innerHTML || '';
      
      // Convert HTML back to markdown before saving
      const markdownContent = convertHtmlToMarkdown(currentContent);
      
      // Call the API to update the scene prose
      const result = await api.updateSceneProseKeepSummary(novelId, chapterId, sceneId, markdownContent);
      
      if (result.success) {
        // Update the local scene data to reflect saved changes
        setScene(prevScene => ({
          ...prevScene,
          content: markdownContent
        }));
        
        setHasUnsavedChanges(false);
        setShowSaveNotification(false);
        setSaveStatus('saved');
        
        // Clear saved status after delay
        setTimeout(() => {
          setSaveStatus('');
        }, 2000);
      } else {
        setError("Failed to save changes. Please try again.");
        setSaveStatus('');
      }
    } catch (error) {
 
    } finally {
      setSaving(false);
    }
  };
  
  // Function to discard AI edits
  const handleDiscardAiEdit = async () => {
    try {
      // Refresh the scene data from the server to get original content
      const scenesResult = await api.getScenesByChapterId(chapterId);
      if (scenesResult.success) {
        const foundScene = scenesResult.scenes.find(
          sc => sc.id.toString() === sceneId.toString()
        );
        if (foundScene) {
          // Set the editor's content directly using the quill API
          const html = convertMarkdownToHtml(foundScene.content || '');
          if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            editor.clipboard.dangerouslyPasteHTML(html);
          }
          
          setShowSaveNotification(false);
          setHasUnsavedChanges(false);
        }
      }
    } catch (error) {
      console.error("Error reverting changes:", error);
      setError("An error occurred while reverting changes.");
    }
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 
    'blockquote', 'list', 'bullet', 'ai-assistant'
  ];

  /**
   * Convert markdown formatting to HTML for the editor
   * @param {string} markdown - Text with markdown formatting
   * @returns {string} - HTML formatted text
   */
  const convertMarkdownToHtml = (markdown) => {
    if (!markdown) return '';
    
    // Replace asterisks with italic HTML
    let html = markdown.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Handle empty lines (preserve blank lines as empty paragraphs)
    // First, normalize line endings
    html = html.replace(/\r\n/g, '\n');

    // Debug logging
    console.log("Original markdown line count:", markdown.split('\n').length);
    console.log("First few paragraphs:", markdown.split('\n\n').slice(0, 3));
    
    // Split by paragraph markers (double newlines)
    const paragraphs = html.split(/\n\n+/);
    console.log("Paragraph count:", paragraphs.length);
    
    // Process each paragraph with proper HTML
    const processedHtml = paragraphs.map((paragraph, i) => {
      // For empty paragraphs, add a non-breaking space to ensure Quill preserves it
      if (!paragraph.trim()) {
        console.log(`Paragraph ${i} is empty, adding &nbsp;`);
        return '<p>&nbsp;</p>';
      }
      
      // Convert single newlines within paragraphs to <br> tags
      const withLineBreaks = paragraph.replace(/\n/g, '<br>');
      return `<p>${withLineBreaks}</p>`;
    }).join('');
    
    console.log("Generated HTML paragraphs:", processedHtml.match(/<p>/g)?.length || 0);
    
    return processedHtml;
  };

  /**
   * Convert HTML formatting back to markdown
   * @param {string} html - HTML formatted text
   * @returns {string} - Text with markdown formatting
   */
  const convertHtmlToMarkdown = (html) => {
    if (!html) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Function to process nodes recursively
    const processNode = (node) => {
      let result = '';
      
      if (node.nodeType === Node.TEXT_NODE) {
        // Text node - return its content
        return node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Element node - process based on tag
        const nodeName = node.nodeName.toLowerCase();
        
        if (nodeName === 'p') {
          // Check if this is an empty paragraph (only contains &nbsp; or is empty)
          const onlyNbsp = node.innerHTML === '&nbsp;' || node.innerHTML === '' || !node.textContent.trim();
          if (onlyNbsp) {
            // This is a blank line
            return '\n\n';
          }
          
          // Process paragraph content
          const paragraphContent = Array.from(node.childNodes).map(processNode).join('');
          return paragraphContent + '\n\n';
        } else if (nodeName === 'br') {
          return '\n';
        } else if (nodeName === 'em' || nodeName === 'i') {
          // Process italic text
          const italicContent = Array.from(node.childNodes).map(processNode).join('');
          return `*${italicContent}*`;
        } else if (nodeName === 'strong' || nodeName === 'b') {
          // Process bold text (if needed)
          const boldContent = Array.from(node.childNodes).map(processNode).join('');
          return boldContent; // We don't have bold in current format, just return content
        } else if (nodeName === 'blockquote') {
          const quoteContent = Array.from(node.childNodes).map(processNode).join('');
          return quoteContent;
        } else {
          // Default case for other elements
          return Array.from(node.childNodes).map(processNode).join('');
        }
      }
      
      return '';
    };
    
    // Process the entire content
    let markdown = processNode(tempDiv);
    
    // Clean up extra newlines, but preserve double newlines for paragraph breaks
    markdown = markdown.replace(/\n{3,}/g, '\n\n').trim();
    
    return markdown;
  };

  if (loading) {
    return <div className="loading">Loading prose...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="scene-prose-view">
      <div className="prose-section">
        <div className="prose-header">
          <div className="prose-actions">
            {saveStatus === 'saving' && (
              <span className="save-status saving">Saving...</span>
            )}
            {saveStatus === 'saved' && (
              <span className="save-status saved">Saved</span>
            )}
            {hasUnsavedChanges && !saveStatus && (
              <span className="save-status unsaved">Unsaved Changes</span>
            )}

            {showSaveNotification && (
              <div className="ai-edit-actions">
                <span className="unsaved-changes-alert">Unsaved AI changes</span>
                <button 
                  className="discard-ai-btn" 
                  onClick={handleDiscardAiEdit}
                  disabled={savingAiEdit}
                >
                  Discard
                </button>
              </div>
            )}
          </div>
        </div>

        {!isReadOnly() &&
        <ReactQuill
          ref={quillRef}
          theme="snow"
          defaultValue={initialContent}
          onChange={handleContentChange}
          modules={modules}
          formats={formats}
          placeholder="Write your scene prose here..."
          className="prose-editor"
          preserveWhitespace={true}
        />}

        {isReadOnly() &&
        <div style={{
          textAlign: "justify", 
          fontFamily: "Georgia",
          lineHeight: "30px",
          fontSize: "18px",
          textIndent: "30px"
        }} className="read-only-content" dangerouslySetInnerHTML={{ __html: initialContent }} />
        }
        
        {/* AI Edit Modal */}
        <AIEditModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedText={selectedText}
          onSubmit={handleAIEditSubmit}
          isProcessing={isProcessingAI}
        />
      </div>
    </div>
  );
}

export default SceneProseView;
